// Frame as an installed app with web push — the client half of
// backend app/services/push.py. The service worker (public/sw.js) only shows
// notifications; it caches nothing.
import api from '@meno/ui/lib/api'

export type PushState =
  | 'unsupported'      // this browser can't do web push at all
  | 'needs-install'    // iPhone/iPad Safari: push works only once added to Home Screen
  | 'unavailable'      // the server has no push keys configured
  | 'denied'           // the user blocked notifications for Frame
  | 'off'
  | 'on'

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches
  || (navigator as unknown as { standalone?: boolean }).standalone === true

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

/** Register the worker once at startup (production builds only). */
export function registerFrameServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* push just stays off */ })
  })
}

let configCache: Promise<{ enabled: boolean; public_key: string | null }> | null = null
function pushConfig() {
  configCache ??= api.get('/push/config').then(r => r.data).catch(() => ({ enabled: false, public_key: null }))
  return configCache
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration()
  return reg ? reg.pushManager.getSubscription() : null
}

export async function getPushState(): Promise<PushState> {
  if (!supported()) return isIOS() && !isStandalone() ? 'needs-install' : 'unsupported'
  const cfg = await pushConfig()
  if (!cfg.enabled || !cfg.public_key) return 'unavailable'
  if (Notification.permission === 'denied') return 'denied'
  return (await currentSubscription()) ? 'on' : 'off'
}

// Returned as a plain ArrayBuffer: `applicationServerKey` is typed
// BufferSource, which a Uint8Array<ArrayBufferLike> no longer satisfies
// under TS 5.7+ lib types (it might be a SharedArrayBuffer).
function keyBytes(base64url: string): ArrayBuffer {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4)
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out.buffer
}

/** Ask permission, subscribe this browser and register it with the server. */
export async function enablePush(): Promise<PushState> {
  const cfg = await pushConfig()
  if (!cfg.enabled || !cfg.public_key) return 'unavailable'
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'
  const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register('/sw.js'))
  await navigator.serviceWorker.ready
  const sub = (await reg.pushManager.getSubscription())
    ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(cfg.public_key) }))
  const json = sub.toJSON()
  await api.post('/push/subscriptions', { endpoint: json.endpoint, keys: json.keys })
  return 'on'
}

export async function disablePush(): Promise<PushState> {
  const sub = await currentSubscription()
  if (sub) {
    await api.post('/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {})
    await sub.unsubscribe().catch(() => false)
  }
  return 'off'
}

export async function sendTestPush(): Promise<number> {
  const { data } = await api.post('/push/test')
  return data?.sent ?? 0
}

// ── Install prompt ──────────────────────────────────────────────────────────
// Chrome/Android fire `beforeinstallprompt` once; keep it so a button can
// offer "Install" later. iOS has no prompt — the UI explains Share → Add to
// Home Screen instead.
type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> }
let deferredInstall: InstallPromptEvent | null = null
const installListeners = new Set<() => void>()
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredInstall = e as InstallPromptEvent
    installListeners.forEach(l => l())
  })
  window.addEventListener('appinstalled', () => {
    deferredInstall = null
    installListeners.forEach(l => l())
  })
}
export const canPromptInstall = () => deferredInstall !== null
export const needsIOSInstallHint = () => isIOS() && !isStandalone()
export function onInstallAvailabilityChange(fn: () => void): () => void {
  installListeners.add(fn)
  return () => installListeners.delete(fn)
}
export async function promptInstall(): Promise<boolean> {
  if (!deferredInstall) return false
  await deferredInstall.prompt()
  const { outcome } = await deferredInstall.userChoice
  deferredInstall = null
  installListeners.forEach(l => l())
  return outcome === 'accepted'
}
